import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Config } from '../../../config/config';
import { ServiceRouter } from '../../../services/core/http-service-router.service';
import { SpinnerComponent } from '../../shared/spinner/spinner.component';
import { METHOD_TYPE } from '../../../constants/service.constants';
import * as moment from 'moment';
import { NavigationBean } from '../../../beans/messaging/navigation.bean';
import { Environment } from '../../../config/environment';
import { EditorConfig } from '../../../config/editor-config';
import { ModalService } from '../../../services/core/modal.service';
import { MessagingService } from '../../../services/messaging/messaging.service';
import { FormGroup, FormControl, Validators } from '@angular/forms';
import { AppUtil } from '../../../utils/app.util';
import { DataCarrierService } from 'src/app/services/core/data-carrier.service';
import { AnalyticsBean } from 'src/app/beans/analytics/analytics-module.bean';
import { ProfileRelatedInfoBean } from 'src/app/beans/account/account-module.bean';
import { EheAnalyticsDirective } from '../../shared/ehe-analytics/ehe-analytics.directive';
import { Editor, Toolbar } from 'ngx-editor';

@Component({
	selector: 'app-message',
	templateUrl: './message.component.html',
	styleUrls: ['./message.component.scss'],
})
/**
 * Messaging Component
 */
export class MessageComponent implements OnInit {
	/**
	 * Flag to indicate if its inbox
	 */
	inbox: boolean = false;
	/**
	 * Message id - whose message details are displayed
	 */
	messageId: any;
	/**
	 * Contains message details
	 */
	messageDetails: any;
	/**
	 * Flag to show or close reply block
	 */
	showReplyBlock: boolean = false;
	/**
	 * Total message count
	 */
	totalMessages: any;
	/**
	 * Current index of list - current message id shown in list
	 */
	currentIndex: any;
	/**
	 * List of messages
	 */
	messages: any;
	/**
	 * Contains navigation details
	 */
	navigationDetails: NavigationBean = new NavigationBean();
	/**
	 * Parent message id - required by reply block
	 */
	parentMessageId: number;
	/**
	 * Contains config for text editor
	 */
	editorConfig = EditorConfig.config;
	/**
	 * Folder id to indicate inbox or sent
	 */
	folderId: number;
	/**
	 * Compose message form - required by reply block
	 */
	composeMessageForm: FormGroup;
	/**
	 * Flag to indicate if browser is IE
	 */
	isIE: boolean = false;
	/**
	 * Contains body of reply block
	 */
	body: any = '';
	/**
	 * Reply address
	 */
	replyToAddress = '';
	/**
	 * Contains user name - required by reply block
	 */
	userName: any;
	/**
	 * Contains analytics data
	 */
	analyticsData: AnalyticsBean;

	editor: Editor;

	html: '';

	toolbar: Toolbar = [
		['bold', 'italic'],
		['underline', 'strike'],
		['code', 'blockquote'],
		['ordered_list', 'bullet_list'],
		[{ heading: ['h1', 'h2', 'h3', 'h4', 'h5', 'h6'] }],
		['link', 'image'],
		['text_color', 'background_color'],
		['align_left', 'align_center', 'align_right', 'align_justify'],
	];

	/**
	 * Message component - constructor
	 * @param config URL Configuration
	 * @param serviceRouter Service Router
	 * @param spinner Spinner Component
	 * @param routeParams Activated route to capture route
	 * @param router Angular route
	 * @param environment Environment
	 * @param modalService Modal Service
	 * @param messageService Messaging Service
	 * @param dataCarrierService Data Carrier Service
	 * @param eheAnalyticsDirective Analytics Directive
	 */
	constructor(
		private config: Config,
		private serviceRouter: ServiceRouter,
		private spinner: SpinnerComponent,
		private routeParams: ActivatedRoute,
		public router: Router,
		private environment: Environment,
		private modalService: ModalService,
		private messageService: MessagingService,
		private dataCarrierService: DataCarrierService,
		private eheAnalyticsDirective: EheAnalyticsDirective
	) {
		/**
		 * Captures information based on route
		 */
		this.routeParams.params.subscribe(params => {
			this.inbox = params.section && params.section === 'inbox';
			const messageParams = JSON.parse(atob(params.message_params));
			this.messageId = +messageParams.messageId;
			this.totalMessages = +messageParams.totalMessages;
			this.currentIndex = +messageParams.currentIndex;
			this.folderId = +messageParams.folderId;
			this.getMessages();
			if (this.messageId) {
				this.getMessageDetails(this.messageId);
			}
		});
		this.analyticsData = new AnalyticsBean();
		this.analyticsData.componentName = 'Message';
		this.analyticsData.pageUrl = '/messaging/mailbox/inbox/message';
	}

	/**
	 * Gets list of messages
	 */
	getMessages() {
		this.spinner.startLoader();
		this.serviceRouter
			.makeRequest(
				this.environment.getEnvironment().EPMS_PROXY_BASE_URL,
				this.config.url.messagingApi.getMessages + `?folderID=${this.folderId}`,
				METHOD_TYPE.SECURED_GET,
				''
			)
			.subscribe(
				data => {
					this.spinner.stopLoader();
					this.messages = data.message;
					this.presetNavs();
				},
				_ => {
					this.spinner.stopLoader();
				}
			);
	}

	/**
	 * Component initialization
	 */
	ngOnInit() {
		this.editor = new Editor();
		this.initializeComposeMessageForm();
		if (AppUtil.isIE()) {
			this.isIE = true;
		}
	}

	/**
	 * Initializes compose message form
	 * Form controls are msgBody
	 */
	initializeComposeMessageForm() {
		this.composeMessageForm = new FormGroup({
			msgBody: new FormControl('', Validators.compose([Validators.required])),
		});
	}

	/**
	 * Method to set previous and next message id,
	 * and set flags if previous and next navigation
	 * are possible or not
	 */
	presetNavs() {
		this.navigationDetails.disableLeftNav = this.currentIndex === 1;
		this.navigationDetails.disableRightNav = this.currentIndex === this.totalMessages;
		if (!this.navigationDetails.disableLeftNav) {
			this.navigationDetails.leftNavValue = this.messages[this.currentIndex - 2].id;
		}
		if (!this.navigationDetails.disableRightNav) {
			this.navigationDetails.rightNavValue = this.messages[this.currentIndex].id;
		}
	}

	/**
	 * Gets message details for given message id
	 * @param messageId message id whose details has to be fetched
	 */
	getMessageDetails(messageId) {
		this.spinner.startLoader();
		this.serviceRouter
			.makeRequest(
				this.environment.getEnvironment().EPMS_PROXY_BASE_URL,
				this.config.url.messagingApi.getMessages + `/${messageId}`,
				METHOD_TYPE.SECURED_GET,
				''
			)
			.subscribe(
				data => {
					this.spinner.stopLoader();
					this.messageDetails = data.message;
					this.parentMessageId = data.parentMessageId;
				},
				_ => {
					this.spinner.stopLoader();
				}
			);
	}

	/**
	 * Gets the formatted date
	 * @param date input date
	 */
	getDate(date) {
		return moment(date, 'YYYY-MM-DD HH:mm:ss').format('MMM DD, YYYY, hh:mm A');
	}

	/**
	 * Opens reply block
	 */
	openReplyBlock() {
		this.analyticsData.id = 'Open reply block';
		this.analyticsData.redirectedTo = '/messaging/mailbox/inbox/message';
		this.analyticsData.placement = 'Messaging inbox message';
		this.dataCarrierService.getChannel().subscribe(data => {
			const profileRelatedInfo: ProfileRelatedInfoBean = data.profile;
			this.userName =
				profileRelatedInfo.profileInfo.firstName.trim() + ' ' + profileRelatedInfo.profileInfo.lastName.trim();
		});

		if (this.messageDetails[0].from === this.userName) {
			this.replyToAddress = this.messageDetails[0].to;
		} else {
			this.replyToAddress = this.messageDetails[0].from;
		}
		this.showReplyBlock = true;
	}

	/**
	 * Closes reply block
	 */
	cancelReplyBlock() {
		this.analyticsData.id = 'Cancel reply block';
		this.analyticsData.redirectedTo = '/messaging/mailbox/inbox/message';
		this.analyticsData.placement = 'Messaging inbox message';
		this.showReplyBlock = false;
		this.initializeComposeMessageForm();
	}

	/**
	 * Sends reply to the parent message
	 */
	sendReply() {
		this.analyticsData.id = 'Send reply';
		this.analyticsData.redirectedTo = '/messaging/mailbox/inbox/message';
		this.analyticsData.placement = 'Messaging inbox message';
		this.body = '';
		if (this.isIE) {
			try {
				const temp = document.createElement('div');
				temp.innerHTML = document.getElementById('msgBody').innerHTML;
				this.body = this.composeMessageForm.get('msgBody');
				// this.body = (temp.getElementsByClassName('ngx-editor-textarea')[0].innerHTML);
			} catch (Exception) {}
		}
		if (this.isIE && this.body.length > 0) {
			this.composeMessageForm.controls['msgBody'].setErrors(null);
		}
		if (this.composeMessageForm.valid) {
			const toSend = {};
			// If receiver type is 2 or 3 sending 3 and if its 4 or 5 sending 5
			toSend['toReceiverType'] = this.messageDetails[0].type === 2 || this.messageDetails[0].type === 3 ? 3 : 5;
			toSend['parentMessageId'] = this.parentMessageId;
			toSend['msgSubject'] = this.messageDetails[0].subject;
			if (this.isIE) {
				toSend['msgBody'] = this.body;
			} else {
				toSend['msgBody'] = this.composeMessageForm.getRawValue().msgBody;
			}
			this.serviceRouter
				.makeRequest(
					this.environment.getEnvironment().EPMS_PROXY_BASE_URL,
					this.config.url.messagingApi.getMessages,
					METHOD_TYPE.SECURED_POST,
					toSend
				)
				.subscribe(
					_ => {
						this.cancelReplyBlock();
						this.getMessageDetails(this.parentMessageId);
						this.modalService.openInfoModal(
							this.config.content.errorTitle.ERRTIT001,
							this.config.content.errorType.ERRTYP001,
							this.config.content.messaging.MSGMSG003,
							'OK'
						);
						this.analyticsData.id = this.config.content.messaging.MSGMSG003;
						this.analyticsData.redirectedTo = '/messaging/mailbox/inbox/message';
						this.analyticsData.placement = 'Message reply modal';
						this.eheAnalyticsDirective.captureModalButtonData(this.analyticsData);
					},
					_ => {
						this.cancelReplyBlock();
					}
				);
		}
	}

	/**
	 * Shows next message - on navigation
	 */
	showNextMessage() {
		this.analyticsData.id = 'Show next message';
		this.analyticsData.redirectedTo = '/messaging/mailbox/inbox/message';
		this.analyticsData.placement = 'Messaging inbox message';
		if (!this.navigationDetails.disableRightNav) {
			this.currentIndex++;
			this.presetNavs();
			this.getMessageDetails(this.navigationDetails.rightNavValue);
			this.showReplyBlock = false;
		}
	}

	/**
	 * Shows previous message - on navigation
	 */
	showPreviousMessage() {
		this.analyticsData.id = 'Show previous messsge';
		this.analyticsData.redirectedTo = '/messaging/mailbox/inbox/message';
		this.analyticsData.placement = 'Messaging inbox message';
		if (!this.navigationDetails.disableLeftNav) {
			this.currentIndex--;
			this.presetNavs();
			this.getMessageDetails(this.navigationDetails.leftNavValue);
			this.showReplyBlock = false;
		}
	}

	/**
	 * Deletes the message from system
	 */
	deleteMessage() {
		this.analyticsData.id = 'Delete message';
		this.analyticsData.redirectedTo = '/messaging/mailbox/inbox/message';
		this.analyticsData.placement = 'Messaging inbox message';
		this.spinner.startLoader();
		let messageIds = '';
		this.messageDetails.forEach(message => {
			messageIds += message.id + ',';
		});
		// Removing the last comma
		messageIds = messageIds.replace(/,\s*$/, '');
		this.serviceRouter
			.makeRequest(
				this.environment.getEnvironment().EPMS_PROXY_BASE_URL,
				this.config.url.messagingApi.getMessages + `?MsgIDList=${messageIds}`,
				METHOD_TYPE.SECURED_DELETE,
				''
			)
			.subscribe(
				_ => {
					this.spinner.stopLoader();
					this.modalService.openInfoModal(
						this.config.content.errorTitle.ERRTIT001,
						this.config.content.errorType.ERRTYP001,
						this.config.content.messaging.MSGMSG002,
						'OK'
					);
					this.analyticsData.id = this.config.content.messaging.MSGMSG002;
					this.analyticsData.redirectedTo = '/mailbox/inbox/message';
					this.analyticsData.placement = 'Message reply modal';
					this.eheAnalyticsDirective.captureModalButtonData(this.analyticsData);
					this.router.navigate(['messaging/mailbox/inbox']);
				},
				_ => {
					this.spinner.stopLoader();
				}
			);
	}
}
